<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import { initSipService } from '../../services/sipService';

const store = useStore();
const { currentAccount } = useAccount();
// VoIP só liberado para planos Growth ou superior.
const voipEnabled = computed(() => !!currentAccount.value?.voip_enabled);

// Initialize SIP service with store reference
onMounted(() => {
  initSipService(store);
});

// Auto-register quando a conta tiver VoIP liberado (Growth+). Reativo: cobre o
// caso de a conta carregar depois do mount. autoRegister é idempotente.
watch(
  voipEnabled,
  enabled => {
    if (enabled) store.dispatch('voipCalls/autoRegister');
  },
  { immediate: true }
);

onUnmounted(() => {
  store.dispatch('voipCalls/disconnectSip');
});

const activeCall = computed(() => store.getters['voipCalls/getActiveCall']);
const sipState = computed(() => store.getters['voipCalls/getSipState']);
const isMuted = computed(() => store.getters['voipCalls/isMuted']);
const isOnHold = computed(() => store.getters['voipCalls/isOnHold']);
const isVisible = computed(() => activeCall.value !== null);
const isMinimized = ref(false);
const showKeypad = ref(false);

// Timer
const elapsed = ref(0);
let timerInterval = null;

const startTimer = () => {
  elapsed.value = 0;
  timerInterval = setInterval(() => { elapsed.value += 1; }, 1000);
};
const stopTimer = () => {
  if (timerInterval) clearInterval(timerInterval);
  timerInterval = null;
};

const formatTime = s => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

watch(() => activeCall.value?.state, (newState, oldState) => {
  if (newState === 'in-call' && oldState !== 'in-call') startTimer();
  if (newState === 'ended' || newState === 'failed' || !newState) stopTimer();
  if (newState) isMinimized.value = false;
});

watch(isVisible, val => { if (!val) { stopTimer(); showKeypad.value = false; } });
onUnmounted(() => stopTimer());

const hangup = () => store.dispatch('voipCalls/hangupCall');
const toggleMute = () => store.dispatch('voipCalls/toggleMute');
const toggleHold = () => store.dispatch('voipCalls/toggleHold');
const sendDTMF = tone => store.dispatch('voipCalls/sendDTMF', tone);

const stateLabel = computed(() => {
  switch (activeCall.value?.state) {
    case 'dialing': return 'Discando...';
    case 'ringing': return 'Chamando...';
    case 'in-call': return 'Em chamada';
    case 'ended': return 'Encerrada';
    case 'failed': return 'Falhou';
    default: return '';
  }
});

const stateColor = computed(() => {
  switch (activeCall.value?.state) {
    case 'dialing': case 'ringing': return '#EAB308';
    case 'in-call': return '#10B981';
    case 'ended': return '#64748b';
    case 'failed': return '#EF4444';
    default: return '#c77a41';
  }
});

const dtmfKeys = ['1','2','3','4','5','6','7','8','9','*','0','#'];
</script>

<template>
  <teleport to="body">
    <!-- SIP status indicator (small dot) -->
    <div v-if="!isVisible && voipEnabled" class="sip-indicator" :class="sipState" :title="`SIP: ${sipState}`" />

    <transition name="slide-up">
      <div v-if="isVisible" class="softphone" :class="{ minimized: isMinimized }">
        <!-- Minimized bar -->
        <div v-if="isMinimized" class="minimized-bar" @click="isMinimized = false">
          <span class="min-dot" :style="{ background: stateColor }" />
          <span class="min-name">{{ activeCall?.contact_name || activeCall?.contact_phone }}</span>
          <span class="min-timer">{{ formatTime(elapsed) }}</span>
        </div>

        <!-- Full widget -->
        <div v-else class="softphone-glass">
          <!-- Header -->
          <div class="sp-header">
            <span class="sp-title">CHAMADA ATIVA</span>
            <button class="sp-minimize" @click="isMinimized = true">—</button>
          </div>

          <!-- Contact -->
          <div class="sp-contact">
            <div class="sp-avatar">
              {{ (activeCall?.contact_name || '?')[0].toUpperCase() }}
            </div>
            <div class="sp-name">{{ activeCall?.contact_name || 'Desconhecido' }}</div>
            <div class="sp-phone">{{ activeCall?.contact_phone }}</div>
          </div>

          <!-- Status -->
          <div class="sp-status">
            <div class="sp-state" :style="{ color: stateColor }">{{ stateLabel }}</div>
            <div v-if="activeCall?.state === 'in-call'" class="sp-timer">{{ formatTime(elapsed) }}</div>
            <div v-if="activeCall?.state === 'in-call'" class="sp-wave">
              <span v-for="i in 7" :key="i" :style="{ animationDelay: `${i * 0.08}s` }" />
            </div>
            <div v-if="activeCall?.state === 'dialing' || activeCall?.state === 'ringing'" class="sp-pulse-ring" />
            <div v-if="activeCall?.state === 'failed' && activeCall?.error" class="sp-error">
              {{ activeCall.error }}
            </div>
          </div>

          <!-- DTMF Keypad -->
          <div v-if="showKeypad && activeCall?.state === 'in-call'" class="sp-keypad">
            <button v-for="key in dtmfKeys" :key="key" class="keypad-btn" @click="sendDTMF(key)">{{ key }}</button>
          </div>

          <!-- Controls -->
          <div class="sp-controls">
            <button
              class="ctrl mute"
              :class="{ active: isMuted }"
              title="Mudo"
              @click="toggleMute"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 1a3 3 0 00-3 3v4a3 3 0 006 0V4a3 3 0 00-3-3z" stroke="currentColor" stroke-width="1.5"/><path d="M13 8a5 5 0 01-10 0M8 13v2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </button>
            <button
              class="ctrl hold"
              :class="{ active: isOnHold }"
              title="Espera"
              @click="toggleHold"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="3" y="3" width="3" height="10" rx="1" fill="currentColor"/><rect x="10" y="3" width="3" height="10" rx="1" fill="currentColor"/></svg>
            </button>
            <button class="ctrl hangup" title="Desligar" @click="hangup">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 9.5c0-1.5 3-3 7-3s7 1.5 7 3v1c0 .5-.5 1-1 1h-2c-.5 0-1-.5-1-1v-1.5c-1-.3-2-.5-3-.5s-2 .2-3 .5V10.5c0 .5-.5 1-1 1H2c-.5 0-1-.5-1-1v-1z" fill="currentColor"/></svg>
            </button>
            <button
              class="ctrl keypad"
              :class="{ active: showKeypad }"
              title="Teclado"
              @click="showKeypad = !showKeypad"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="4" cy="4" r="1.5" fill="currentColor"/><circle cx="8" cy="4" r="1.5" fill="currentColor"/><circle cx="12" cy="4" r="1.5" fill="currentColor"/><circle cx="4" cy="8" r="1.5" fill="currentColor"/><circle cx="8" cy="8" r="1.5" fill="currentColor"/><circle cx="12" cy="8" r="1.5" fill="currentColor"/><circle cx="8" cy="12" r="1.5" fill="currentColor"/></svg>
            </button>
          </div>

          <!-- Info bar -->
          <div class="sp-info">
            <div v-if="activeCall?.state === 'in-call'" class="recording">
              <span class="rec-dot" />
              Gravando
            </div>
            <span v-else />
            <span class="provider">SIP direto</span>
          </div>

          <!-- Duration after ended -->
          <div v-if="activeCall?.state === 'ended'" class="sp-ended">
            {{ formatTime(elapsed) }}
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
.sip-indicator {
  position: fixed; bottom: 24px; right: 24px; z-index: 9998;
  width: 10px; height: 10px; border-radius: 50%;
  transition: all 0.3s;
}
.sip-indicator.registered { background: #10B981; box-shadow: 0 0 6px #10B981; }
.sip-indicator.connected { background: #EAB308; }
.sip-indicator.disconnected { background: #64748b; }
.sip-indicator.failed { background: #EF4444; }

.softphone { position: fixed; bottom: 20px; right: 20px; z-index: 9999; width: 260px; }
.softphone.minimized { width: 220px; }

.minimized-bar {
  display: flex; align-items: center; gap: 8px; padding: 8px 12px;
  background: rgba(22,33,62,0.95); border: 1px solid rgba(199,122,65,0.3);
  border-radius: 24px; cursor: pointer; backdrop-filter: blur(20px);
}
.min-dot { width: 8px; height: 8px; border-radius: 50%; animation: pulse-glow 1.5s infinite; }
.min-name { font-size: 11px; font-weight: 600; color: #e2e8f0; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.min-timer { font-size: 11px; font-weight: 700; color: #c77a41; font-variant-numeric: tabular-nums; }

.softphone-glass {
  background: linear-gradient(135deg,rgba(22,33,62,0.96),rgba(30,45,74,0.92));
  border: 1px solid rgba(199,122,65,0.3); border-radius: 14px;
  backdrop-filter: blur(20px); overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.4), 0 0 20px rgba(199,122,65,0.1);
}

.sp-header { padding: 10px 14px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(199,122,65,0.15); }
.sp-title { font-size: 10px; font-weight: 700; color: #c77a41; letter-spacing: 0.08em; }
.sp-minimize { width: 18px; height: 18px; border-radius: 50%; border: 1px solid rgba(100,116,139,0.3); background: none; color: #64748b; font-size: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center; }

.sp-contact { padding: 16px; text-align: center; border-bottom: 1px solid rgba(199,122,65,0.1); }
.sp-avatar { width: 48px; height: 48px; background: linear-gradient(135deg,#c77a41,#995a2a); border-radius: 50%; margin: 0 auto 8px; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 700; color: #fff; }
.sp-name { font-size: 13px; font-weight: 700; color: #e2e8f0; }
.sp-phone { font-size: 11px; color: #c77a41; margin-top: 2px; }

.sp-status { padding: 12px 14px; text-align: center; }
.sp-state { font-size: 11px; font-weight: 600; margin-bottom: 4px; }
.sp-timer { font-size: 24px; font-weight: 800; color: #e2e8f0; font-variant-numeric: tabular-nums; }
.sp-wave { display: flex; justify-content: center; gap: 2px; margin-top: 8px; height: 20px; align-items: flex-end; }
.sp-wave span { width: 3px; background: #c77a41; border-radius: 2px; animation: wave 1s ease-in-out infinite; }
@keyframes wave { 0%,100% { height: 4px; } 50% { height: 18px; } }

.sp-pulse-ring { width: 40px; height: 40px; border-radius: 50%; margin: 8px auto; border: 2px solid #EAB308; animation: pulse-ring 1.2s ease-out infinite; }
@keyframes pulse-ring { 0% { transform: scale(0.8); opacity: 1; } 100% { transform: scale(1.6); opacity: 0; } }

.sp-error { margin-top: 6px; padding: 6px 10px; font-size: 10px; color: #ef4444; background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.2); border-radius: 6px; text-align: center; }

.sp-keypad { display: grid; grid-template-columns: repeat(3,1fr); gap: 4px; padding: 8px 14px; border-top: 1px solid rgba(199,122,65,0.1); }
.keypad-btn { padding: 8px; font-size: 14px; font-weight: 600; background: rgba(42,42,74,0.5); border: 1px solid rgba(42,42,74,0.8); border-radius: 6px; color: #e2e8f0; cursor: pointer; transition: all 0.15s; }
.keypad-btn:hover { background: rgba(199,122,65,0.2); border-color: #c77a41; }

.sp-controls { padding: 12px 14px; display: flex; justify-content: center; gap: 14px; border-top: 1px solid rgba(199,122,65,0.1); }
.ctrl { width: 40px; height: 40px; border-radius: 50%; border: 1px solid rgba(42,42,74,0.8); background: rgba(42,42,74,0.5); color: #94a3b8; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; }
.ctrl:hover { border-color: #c77a41; color: #c77a41; }
.ctrl.active { background: rgba(199,122,65,0.2); border-color: #c77a41; color: #c77a41; }
.ctrl.hangup { background: rgba(239,68,68,0.2); border-color: rgba(239,68,68,0.4); color: #ef4444; width: 48px; height: 48px; }
.ctrl.hangup:hover { background: rgba(239,68,68,0.4); }

.sp-info { padding: 6px 14px 10px; border-top: 1px solid rgba(199,122,65,0.1); display: flex; justify-content: space-between; }
.sp-info span { font-size: 9px; color: #64748b; }
.recording { color: #ef4444 !important; display: flex; align-items: center; gap: 4px; font-size: 9px; }
.rec-dot { width: 5px; height: 5px; background: #ef4444; border-radius: 50%; animation: blink 1s infinite; }
@keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
@keyframes pulse-glow { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }

.sp-ended { text-align: center; padding: 6px 14px 12px; font-size: 12px; color: #64748b; }

.slide-up-enter-active { transition: all 0.3s ease-out; }
.slide-up-leave-active { transition: all 0.2s ease-in; }
.slide-up-enter-from { opacity: 0; transform: translateY(20px); }
.slide-up-leave-to { opacity: 0; transform: translateY(20px); }
</style>
